import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { AI_NOTE_LANGUAGE_DEFAULT, AiNoteLanguageSchema } from './ai/models.ai_preferences';
import {
  DateTimeTzSchema,
  GenericTimelyAccountId,
  GenericTimelyUserId,
  GenericUserID,
  GenericUUID,
  ResponseMetaSchema,
  URLParamsSchema,
} from './models.generic';

extendZodWithOpenApi(z);

/**
 * TIMESHEET PROPOSALS MODELS
 *
 * This module defines the Zod schemas and TypeScript types for the timesheet proposals system.
 * The system allows AI to generate timesheet suggestions based on user activity and memories.
 */

// ================================================
// ENUMS AND CONSTANTS
// ================================================

/**
 * Status enum for timesheet proposals
 */
export const TimesheetProposalStatusEnums = ['pending', 'accepted', 'rejected', 'edited'] as const;
export const TimesheetProposalStatusSchema = z.enum(TimesheetProposalStatusEnums).openapi({
  description: 'Current status of the timesheet proposal',
  example: 'pending',
});

/**
 * Log entry actions for audit trail
 */
export const TimesheetProposalLogActionEnums = [
  'created',
  'status_changed',
  'entry_added',
  'entry_removed',
  'entry_updated',
  'entry_merged',
  'item_updated',
  'item_assigned',
  'item_unassigned',
  'ai_regenerated',
  'note_regenerated',
] as const;
export type TimesheetProposalLogAction = (typeof TimesheetProposalLogActionEnums)[number];
export const TimesheetProposalLogActionSchema = z.enum(TimesheetProposalLogActionEnums).openapi({
  description: 'Type of action performed on the proposal',
  example: 'created',
});

// ================================================
// SHARED SCHEMAS
// ================================================

/**
 * Log entry for timesheet proposal audit trail
 */
export type TimesheetProposalLogEntry = z.infer<typeof TimesheetProposalLogEntrySchema>;
export const TimesheetProposalLogEntrySchema = z
  .object({
    timestamp: DateTimeTzSchema.openapi({
      description: 'When this action occurred',
      example: '2024-06-11T12:00:00.000Z',
    }),
    action: TimesheetProposalLogActionSchema,
    details: z
      .object({})
      .passthrough()
      .optional()
      .openapi({
        description: 'Additional details about the action',
        example: { previous_status: 'pending', new_status: 'accepted' },
      }),
    user_id: GenericUserID.nullable().optional().openapi({
      description: 'User who performed the action (null if system-generated)',
    }),
  })
  .openapi({
    description: 'Audit log entry for tracking proposal changes',
  });

/**
 * Memory reference for linking timesheet items to memories
 */
export const MemoryReferenceSchema = z.string().uuid().openapi({
  description: 'UUID of a memory that contributed to this time block',
  example: '123e4567-e89b-12d3-a456-426614174000',
});

/**
 * Timely project ID reference
 */

export const TimelyProjectIdSchema = z.coerce.number().int().min(1).nullable().openapi({
  description: 'Timely project ID (positive = assigned project, null = unassigned)',
  example: 12345,
});

/**
 * Timely label ID reference
 */
export const TimelyLabelIdSchema = z.coerce.number().int().min(1).openapi({
  description: 'Timely label ID',
  example: 67890,
});

// ================================================
// BASE SCHEMAS
// ================================================

/**
 * Base schema for timesheet proposals
 */
export const TimesheetProposalBaseSchema = z.object({
  id: GenericUUID.openapi({
    description: 'Unique identifier for the timesheet proposal',
  }),
  date: z.string().date().openapi({
    description: 'Date this proposal is for (YYYY-MM-DD)',
    example: '2024-06-11',
  }),
  timely_user_id: GenericTimelyUserId.openapi({
    description: 'Timely user ID this proposal is for',
  }),
  user_id: GenericUserID.openapi({
    description: 'TIC user ID (auth.users reference)',
  }),
  timely_account_id: z.coerce.number().int().min(1).openapi({
    description: 'Timely account ID this proposal belongs to',
    example: 12345,
  }),
  status: TimesheetProposalStatusSchema.default('pending'),
  log: z.array(TimesheetProposalLogEntrySchema).default([]).openapi({
    description: 'Audit trail of all interactions with this proposal',
  }),
  metadata: z
    .object({})
    .passthrough()
    .default({})
    .openapi({
      description: 'Additional metadata for the proposal',
      example: { ai_confidence: 0.85, generation_duration_ms: 2340 },
    }),
  created_at: DateTimeTzSchema.openapi({
    description: 'When the proposal was created',
  }),
  updated_at: DateTimeTzSchema.nullable().optional().openapi({
    description: 'When the proposal was last updated',
  }),
  deleted_at: DateTimeTzSchema.nullable().optional().openapi({
    description: 'When the proposal was soft-deleted (null if active)',
  }),
});

/**
 * Base schema for timesheet proposal entries
 */
export const TimesheetProposalEntryBaseSchema = z.object({
  id: GenericUUID.openapi({
    description: 'Unique identifier for the proposal entry',
  }),
  timesheet_proposal_id: GenericUUID.openapi({
    description: 'ID of the parent timesheet proposal',
  }),
  date: z.string().date().openapi({
    description: 'Date this entry is for (YYYY-MM-DD)',
    example: '2024-06-11',
  }),
  timely_project_id: TimelyProjectIdSchema,
  timely_label_ids: z
    .array(TimelyLabelIdSchema)
    .default([])
    .openapi({
      description: 'Array of Timely label IDs associated with this entry',
      example: [123, 456, 789],
    }),
  started_at: DateTimeTzSchema.nullable()
    .optional()
    .describe(
      'Entry timeline footprint start. Null for legacy empty entries. Always set for new entries.'
    ),
  ended_at: DateTimeTzSchema.nullable()
    .optional()
    .describe('Entry timeline footprint end. Must satisfy started_at < ended_at when set.'),
  hour_id: z.coerce.number().int().min(1).nullable().optional().openapi({
    description:
      'Reference to logged hour entry ID in main system. NULL for draft entries, populated when entry is accepted/logged.',
    example: 123456789,
  }),
  note: z.string().nullable().optional().openapi({
    description: 'Note for this daily entry',
    example: 'Working on user authentication feature',
  }),
  duration_minutes: z.coerce.number().int().min(0).nullable().optional().openapi({
    description: 'Duration in minutes for this entry. Can be set independently of time blocks.',
    example: 480,
  }),
  padding_minutes: z.number().int().openapi({
    description:
      'Padding minutes for this entry, used to adjust total duration without changing item times',
    example: 480,
  }),
  resource_id: GenericUUID.nullable().optional().openapi({
    description:
      'Reference to an external resource (task, issue, etc.) that this entry is related to',
    example: '123e4567-e89b-12d3-a456-426614174000',
  }),
  metadata: z
    .object({})
    .passthrough()
    .default({})
    .openapi({
      description: 'Additional metadata for the entry',
      example: { ai_suggested_duration: 480 },
    }),
  created_at: DateTimeTzSchema.openapi({
    description: 'When the entry was created',
  }),
  updated_at: DateTimeTzSchema.nullable().optional().openapi({
    description: 'When the entry was last updated',
  }),
  deleted_at: DateTimeTzSchema.nullable().optional().openapi({
    description: 'When the entry was soft-deleted (null if active)',
  }),
});

/**
 * Base schema for timesheet proposal entry items (time blocks)
 */
export const TimesheetProposalEntryItemBaseSchema = z.object({
  id: GenericUUID.openapi({
    description: 'Unique identifier for the entry item',
  }),
  timesheet_proposal_id: z.string().uuid().describe('Denormalized proposal reference; non-null.'),
  timesheet_proposal_entry_id: z
    .string()
    .uuid()
    .nullable()
    .describe(
      'Provenance: the entry this item was originally grouped into at AI generation time. NOT current ownership — current ownership is derived from timestamp containment against entry ranges.'
    ),
  started_at: DateTimeTzSchema.nullable().optional().openapi({
    description: 'Start timestamp with timezone for this time block',
    example: '2024-06-11T09:00:00.000Z',
  }),
  ended_at: DateTimeTzSchema.nullable().optional().openapi({
    description: 'End timestamp with timezone for this time block',
    example: '2024-06-11T11:30:00.000Z',
  }),
  memory_ids: z
    .array(MemoryReferenceSchema)
    .default([])
    .openapi({
      description: 'Array of memory UUIDs that contributed to this time block',
      example: ['123e4567-e89b-12d3-a456-426614174000', '987e6543-e21b-98d7-a654-624136174987'],
    }),
  note: z.string().nullable().optional().openapi({
    description: 'Note for this specific time block',
    example: 'Code review and debugging session',
  }),
  metadata: z
    .object({})
    .passthrough()
    .default({})
    .openapi({
      description: 'Additional metadata for the item',
      example: { ai_confidence: 0.92, memory_match_score: 0.87 },
    }),
  created_at: DateTimeTzSchema.openapi({
    description: 'When the item was created',
  }),
  updated_at: DateTimeTzSchema.nullable().optional().openapi({
    description: 'When the item was last updated',
  }),
  deleted_at: DateTimeTzSchema.nullable().optional().openapi({
    description: 'When the item was soft-deleted (null if active)',
  }),
});

// ================================================
// FULL SCHEMAS WITH RELATIONSHIPS
// ================================================

/**
 * Complete entry item schema
 */
export type TimesheetProposalEntryItem = z.infer<typeof TimesheetProposalEntryItemSchema>;
export const TimesheetProposalEntryItemSchema = TimesheetProposalEntryItemBaseSchema;

/**
 * Read-shape for a proposal entry. Items aren't nested here — they live at
 * the proposal level (TimesheetProposalSchema.items) and are attributed to
 * entries client-side via timestamp containment.
 */
export type TimesheetProposalEntry = z.infer<typeof TimesheetProposalEntrySchema>;
export const TimesheetProposalEntrySchema = TimesheetProposalEntryBaseSchema;

/**
 * Complete proposal schema with optional entries
 */
export type TimesheetProposal = z.infer<typeof TimesheetProposalSchema>;
export const TimesheetProposalSchema = TimesheetProposalBaseSchema.extend({
  entries: z.array(TimesheetProposalEntrySchema).optional().openapi({
    description:
      'Daily entries within this proposal (loaded when fetching full proposal). Items are NOT nested under entries — they are returned at the top level via `items` and grouped by timestamp containment client-side.',
  }),
  items: z.array(TimesheetProposalEntryItemSchema).optional().openapi({
    description:
      'Every item belonging to this proposal — assigned (contained by some entry) and unassigned alike. Membership in a particular entry is derived client-side via timestamp containment. The `timesheet_proposal_entry_id` FK is provenance only and not authoritative for grouping.',
  }),
});

// ================================================
// WRITE/CREATE SCHEMAS
// ================================================

/**
 * Schema for creating entry items
 */
export type CreateTimesheetProposalEntryItem = z.infer<
  typeof CreateTimesheetProposalEntryItemSchema
>;
export const CreateTimesheetProposalEntryItemSchema = z
  .object({
    started_at: DateTimeTzSchema.nullable().optional(),
    ended_at: DateTimeTzSchema.nullable().optional(),
    memory_ids: z.array(MemoryReferenceSchema).default([]),
    note: z.string().nullable().optional(),
    metadata: z.object({}).passthrough().default({}),
  })
  .openapi({
    description: 'Data for creating a new entry item',
  });

const CreateTimesheetProposalEntryBaseShape = {
  date: z.string().date(),
  timely_project_id: TimelyProjectIdSchema,
  timely_label_ids: z.array(TimelyLabelIdSchema).default([]),
  hour_id: z.coerce.number().int().min(1).nullable().optional(),
  note: z.string().nullable().optional(),
  duration_minutes: z.coerce.number().int().min(0).nullable().optional(),
  padding_minutes: z.number().int().optional().default(0),
  resource_id: GenericUUID.nullable().optional(),
  metadata: z.object({}).passthrough().default({}),
  started_at: DateTimeTzSchema.nullable()
    .optional()
    .describe(
      'Entry timeline footprint start. Required for "claim empty time" entries with no items; null only for legacy empty entries with no timestamps.'
    ),
  ended_at: DateTimeTzSchema.nullable()
    .optional()
    .describe('Entry timeline footprint end. See started_at.'),
};

// Shared by both create-entry schemas: an entry needs a timeline footprint,
// either an explicit range or a positive duration.
const entryHasRangeOrDuration = (data: {
  started_at?: unknown;
  ended_at?: unknown;
  duration_minutes?: number | null;
}): boolean =>
  (data.started_at != null && data.ended_at != null) || (data.duration_minutes ?? 0) > 0;

/**
 * Schema for creating entries as part of a full-proposal POST (AI generation
 * path). Accepts nested `items` so the AI lambda can submit entries with their
 * time blocks in one payload.
 */
export type CreateTimesheetProposalEntry = z.infer<typeof CreateTimesheetProposalEntrySchema>;
export const CreateTimesheetProposalEntrySchema = z
  .object({
    ...CreateTimesheetProposalEntryBaseShape,
    items: z.array(CreateTimesheetProposalEntryItemSchema).default([]),
  })
  .refine(data => (data.items?.length ?? 0) > 0 || entryHasRangeOrDuration(data), {
    message:
      'Entry must have at least one of: items, started_at + ended_at, or a positive duration_minutes',
  })
  .openapi({
    description: 'Data for creating a new proposal entry (with optional nested items)',
  });

/**
 * Schema for creating complete proposals
 */
export type CreateTimesheetProposal = z.infer<typeof CreateTimesheetProposalSchema>;
export const CreateTimesheetProposalSchema = z
  .object({
    date: z.string().date(),
    timely_user_id: GenericTimelyUserId,
    timely_account_id: z.coerce.number().int().min(1),
    metadata: z.object({}).passthrough().default({}),
    entries: z.array(CreateTimesheetProposalEntrySchema).default([]),
  })
  .openapi({
    description: 'Data for creating a new timesheet proposal',
    example: {
      date: '2024-06-11',
      timely_user_id: 728755,
      timely_account_id: 12345,
      entries: [
        {
          date: '2024-06-11',
          timely_project_id: 12345,
          timely_label_ids: [123, 456],
          note: 'Working on authentication',
          items: [
            {
              started_at: '2024-06-11T09:00:00.000Z',
              ended_at: '2024-06-11T11:30:00.000Z',
              memory_ids: ['123e4567-e89b-12d3-a456-426614174000'],
              note: 'Code review session',
            },
          ],
        },
      ],
    },
  });

/**
 * Schema for creating a single entry within an existing proposal. Unlike the
 * full-proposal path, this endpoint doesn't accept nested items — entries are
 * authored from a time range (drag-to-create) and items are managed
 * separately at the proposal level.
 */
export type CreateTimesheetProposalEntryOnly = z.infer<
  typeof CreateTimesheetProposalEntryOnlySchema
>;
// No `.strict()`: a stray `items` from a stale FE bundle is stripped by Zod's
// default parsing rather than rejected. Safe because this endpoint is only ever
// called with a range (drag-to-create); items have always lived at proposal level.
export const CreateTimesheetProposalEntryOnlySchema = z
  .object(CreateTimesheetProposalEntryBaseShape)
  .refine(entryHasRangeOrDuration, {
    message:
      'Entry must have at least one of: started_at + ended_at, or a positive duration_minutes',
  })
  .openapi({
    description: 'Data for creating a new entry within an existing timesheet proposal',
    example: {
      date: '2024-06-11',
      timely_project_id: 12345,
      timely_label_ids: [123, 456],
      note: 'Working on user interface updates',
      started_at: '2024-06-11T14:00:00.000Z',
      ended_at: '2024-06-11T16:30:00.000Z',
    },
  });

// ================================================
// UPDATE SCHEMAS
// ================================================

/**
 * Schema for updating proposal status
 */
export type UpdateTimesheetProposalStatus = z.infer<typeof UpdateTimesheetProposalStatusSchema>;
export const UpdateTimesheetProposalStatusSchema = z
  .object({
    status: TimesheetProposalStatusSchema,
    log_entry: TimesheetProposalLogEntrySchema.optional().openapi({
      description: 'Optional log entry to record with the status change',
    }),
  })
  .openapi({
    description: 'Data for updating a proposal status',
    example: {
      status: 'accepted',
      log_entry: {
        timestamp: '2024-06-11T12:00:00.000Z',
        action: 'status_changed',
        details: { reason: 'User approved the suggestion' },
      },
    },
  });

/**
 * Schema for patching entry
 */
export type PatchTimesheetProposalEntry = z.infer<typeof PatchTimesheetProposalEntrySchema>;
export const PatchTimesheetProposalEntrySchema = z
  .object({
    // Entries carry a NOT NULL timely_project_id. "Unassign" is an item-level
    // operation (move items to the Unassigned bucket); PATCHing an entry's
    // project to null is not a valid operation.
    timely_project_id: z.coerce.number().int().min(1).optional(),
    timely_label_ids: z.array(TimelyLabelIdSchema).default([]).optional(),
    note: z.string().nullable().optional(),
    duration_minutes: z.coerce.number().int().min(0).nullable().optional(),
    padding_minutes: z.coerce.number().int().optional(),
    hour_id: z.coerce.number().int().min(1).nullable().optional(),
    resource_id: GenericUUID.nullable().optional(),
    started_at: DateTimeTzSchema.nullable()
      .optional()
      .describe(
        'Entry timeline footprint start. Null only for legacy empty entries; non-null pairs (started_at, ended_at) must satisfy started_at < ended_at.'
      ),
    ended_at: DateTimeTzSchema.nullable()
      .optional()
      .describe('Entry timeline footprint end. See started_at.'),
    updated_at: DateTimeTzSchema.nullable().optional(),
  })
  .openapi({
    description: 'Data for patching an existing proposal entry',
    example: {
      timely_project_id: 12345,
      timely_label_ids: [123, 456],
      note: 'Updated work description',
      padding_minutes: 480,
      hour_id: 123456789,
      resource_id: '123e4567-e89b-12d3-a456-426614174000',
    },
  });

// ================================================
// LIST/SUMMARY SCHEMAS
// ================================================

/**
 * Lightweight proposal schema for list views
 */
export type TimesheetProposalListItem = z.infer<typeof TimesheetProposalListItemSchema>;
export const TimesheetProposalListItemSchema = TimesheetProposalBaseSchema.pick({
  id: true,
  date: true,
  status: true,
  created_at: true,
  updated_at: true,
})
  .extend({
    entry_count: z.number().int().min(0).optional().openapi({
      description: 'Number of entries in this proposal',
      example: 5,
    }),
    total_duration_minutes: z.number().int().min(0).optional().openapi({
      description: 'Total duration of all items in minutes (calculated from started_at/ended_at)',
      example: 2400,
    }),
  })
  .openapi({
    description: 'Lightweight proposal data for list displays',
  });

// ================================================
// QUERY SCHEMAS
// ================================================

/**
 * Query parameters for listing proposals
 */
export type GetTimesheetProposalsQuery = z.infer<typeof GetTimesheetProposalsQuerySchema>;
export const GetTimesheetProposalsQuerySchema = z
  .object({
    timely_account_id: GenericTimelyAccountId.optional(),
    status: TimesheetProposalStatusSchema.optional(),
    date_from: z.string().date().optional().openapi({
      description: 'Filter proposals on or after this date',
    }),
    date_to: z.string().date().optional().openapi({
      description: 'Filter proposals on or before this date',
    }),
    timely_user_id: GenericTimelyUserId.optional(),
    page: URLParamsSchema.page.default(1).optional(),
    limit: URLParamsSchema.limit.default(20).optional(),
  })
  .openapi({
    description: 'Query parameters for listing timesheet proposals',
  });

/**
 * Schema for triggering AI generation
 */
export type TriggerTimesheetProposalGeneration = z.infer<
  typeof TriggerTimesheetProposalGenerationSchema
>;
export const TriggerTimesheetProposalGenerationSchema = z
  .object({
    date: z.string().date(),
    timely_user_id: GenericTimelyUserId,
    timely_account_id: z.coerce.number().int().min(1),
    // manual means the user manually requested a generation
    // auto means the generation was scheduled (e.g. for email reminders)
    generation_mode: z.enum(['manual', 'auto']).default('manual').optional().openapi({
      description: 'Mode of generation: manual (user-initiated) or auto (scheduled)',
      example: 'manual',
    }),
    force_regenerate: z.boolean().default(false).optional().openapi({
      description: 'Whether to regenerate if a proposal already exists for this date',
      example: false,
    }),
    session_id: z.number().int().nullable().optional().openapi({
      description:
        'Amplitude browser session id for interactive generations. Propagated to the AI service so the backend-emitted Amplitude event joins the same user session.',
      example: 1713700000000,
    }),
    metadata: z
      .object({
        effort: z.enum(['none', 'low', 'medium']).default('low').optional(),
        interval_minutes: z.number().int().min(0).default(30).optional(),
        provider: z.string().default('OpenAI').optional(),
        note_language: AiNoteLanguageSchema.default(AI_NOTE_LANGUAGE_DEFAULT).optional(),
      })
      .passthrough()
      .default({})
      .optional()
      .openapi({
        description:
          'Additional parameters for AI generation, including optional AI service configuration',
        example: {
          effort: 'medium',
          interval_minutes: 15,
          provider: 'openai',
          note_language: 'Spanish',
          include_low_confidence: true,
        },
      }),
  })
  .openapi({
    description: 'Parameters for triggering AI timesheet proposal generation',
    example: {
      date: '2024-06-11',
      timely_user_id: 728755,
      timely_account_id: 12345,
      generation_mode: 'manual',
      force_regenerate: false,
    },
  });

/**
 * Schema for triggering AI note regeneration
 */
export type RegenerateTimesheetProposalNotes = z.infer<
  typeof RegenerateTimesheetProposalNotesSchema
>;
export const RegenerateTimesheetProposalNotesSchema = z
  .object({
    date: z.string().date(),
    timely_account_id: z.coerce.number().int().min(1),
    timely_user_id: GenericTimelyUserId,
    entry_id: GenericUUID.optional().openapi({
      description:
        'Optional entry ID to regenerate note for a single entry. If omitted, regenerates notes for all edited entries on the date.',
    }),
    session_id: z.number().int().nullable().optional().openapi({
      description:
        'Amplitude browser session id for interactive regenerations. Propagated to the AI service for backend-side Amplitude attribution.',
      example: 1713700000000,
    }),
    metadata: z
      .object({
        note_language: AiNoteLanguageSchema.default(AI_NOTE_LANGUAGE_DEFAULT).optional(),
      })
      .passthrough()
      .default({})
      .optional()
      .openapi({
        description: 'Additional parameters for AI note regeneration',
        example: {
          note_language: 'Spanish',
        },
      }),
  })
  .openapi({
    description: 'Parameters for triggering AI note regeneration for edited entries',
    example: {
      date: '2024-06-11',
      timely_account_id: 12345,
      timely_user_id: 728755,
      entry_id: '123e4567-e89b-12d3-a456-426614174000',
      metadata: {
        note_language: 'English',
      },
    },
  });

// ================================================
// RESPONSE SCHEMAS
// ================================================

/**
 * Response for list of proposals
 */
export type GetTimesheetProposalsResponse = z.infer<typeof GetTimesheetProposalsResponseSchema>;
export const GetTimesheetProposalsResponseSchema = z
  .object({
    proposals: z.array(TimesheetProposalListItemSchema),
    meta: ResponseMetaSchema,
  })
  .openapi({
    description: 'Response containing a list of timesheet proposals',
  });

/**
 * Response for single proposal
 */
export type GetTimesheetProposalResponse = z.infer<typeof GetTimesheetProposalResponseSchema>;
export const GetTimesheetProposalResponseSchema = z
  .object({
    proposal: TimesheetProposalSchema,
  })
  .openapi({
    description: 'Response containing a full timesheet proposal with entries and items',
  });

/**
 * Response for created proposal
 */
export type CreateTimesheetProposalResponse = z.infer<typeof CreateTimesheetProposalResponseSchema>;
export const CreateTimesheetProposalResponseSchema = z
  .object({
    proposal: TimesheetProposalSchema,
  })
  .openapi({
    description: 'Response containing the newly created timesheet proposal',
  });

/**
 * Response for trigger generation
 */
export type TriggerTimesheetProposalGenerationResponse = z.infer<
  typeof TriggerTimesheetProposalGenerationResponseSchema
>;
export const TriggerTimesheetProposalGenerationResponseSchema = z
  .object({
    message: z.string(),
    job_id: z.string().optional().openapi({
      description: 'Background job ID for tracking generation progress',
      example: 'job_123e4567-e89b-12d3-a456-426614174000',
    }),
    estimated_completion_time: DateTimeTzSchema.optional().openapi({
      description: 'Estimated time when generation will complete',
    }),
  })
  .openapi({
    description: 'Response for triggering AI proposal generation',
    example: {
      message: 'Timesheet proposal generation started',
      job_id: 'job_123e4567-e89b-12d3-a456-426614174000',
      estimated_completion_time: '2024-06-11T12:05:00.000Z',
    },
  });

/**
 * Response for triggering note regeneration
 */
export type RegenerateTimesheetProposalNotesResponse = z.infer<
  typeof RegenerateTimesheetProposalNotesResponseSchema
>;
export const RegenerateTimesheetProposalNotesResponseSchema = z
  .object({
    message: z.string(),
    job_id: z.string().optional().openapi({
      description: 'Background job ID for tracking regeneration progress',
    }),
    estimated_completion_time: DateTimeTzSchema.optional().openapi({
      description: 'Estimated time when note regeneration will complete',
    }),
  })
  .openapi({
    description: 'Response for triggering AI note regeneration',
    example: {
      message: 'Note regeneration started',
      job_id: 'job_123e4567-e89b-12d3-a456-426614174000',
      estimated_completion_time: '2024-06-11T12:05:00.000Z',
    },
  });

/**
 * Response for created entry
 */
export type CreateTimesheetProposalEntryResponse = z.infer<
  typeof CreateTimesheetProposalEntryResponseSchema
>;
export const CreateTimesheetProposalEntryResponseSchema = z
  .object({
    entry: TimesheetProposalEntrySchema,
    operation_metadata: z
      .object({
        was_reused: z.boolean().openapi({
          description:
            'Whether an existing entry was reused instead of creating a new one (typically for unassigned entries)',
          example: false,
        }),
        action: z.enum(['created_new_entry', 'reused_existing_entry']).openapi({
          description: 'The action that was performed',
          example: 'created_new_entry',
        }),
        message: z.string().openapi({
          description: 'Human-readable description of what happened',
          example: 'Created new timesheet entry',
        }),
      })
      .openapi({
        description: 'Metadata about the operation that was performed',
      }),
  })
  .openapi({
    description: 'Response containing the timesheet proposal entry and operation metadata',
  });

/**
 * Response for deleted entry
 */
export type DeleteTimesheetProposalEntryResponse = z.infer<
  typeof DeleteTimesheetProposalEntryResponseSchema
>;
export const DeleteTimesheetProposalEntryResponseSchema = z
  .object({
    message: z.string().openapi({
      description: 'Confirmation message for the deletion',
      example: 'Timesheet proposal entry deleted successfully',
    }),
    deleted_entry_id: GenericUUID.openapi({
      description: 'ID of the deleted entry',
    }),
  })
  .openapi({
    description: 'Response confirming successful deletion of a timesheet proposal entry',
  });

// ================================================
// ERROR SCHEMAS
// ================================================

/**
 * Validation error for proposal operations
 */
export type TimesheetProposalValidationError = z.infer<
  typeof TimesheetProposalValidationErrorSchema
>;
export const TimesheetProposalValidationErrorSchema = z
  .object({
    message: z.string(),
    errors: z
      .array(
        z.object({
          field: z.string(),
          message: z.string(),
          code: z.string().optional(),
        })
      )
      .optional(),
  })
  .openapi({
    description: 'Validation error response for timesheet proposal operations',
    example: {
      message: 'Invalid proposal data',
      errors: [
        { field: 'date_from', message: 'Must be before date_to', code: 'DATE_RANGE_INVALID' },
        {
          field: 'entries[0].timely_project_id',
          message: 'Project not found',
          code: 'PROJECT_NOT_FOUND',
        },
      ],
    },
  });

/**
 * Conflict error when a proposal already exists
 */
export type TimesheetProposalConflictError = z.infer<typeof TimesheetProposalConflictErrorSchema>;
export const TimesheetProposalConflictErrorSchema = z
  .object({
    message: z.string().openapi({
      example: 'A proposal already exists for this date range',
    }),
    existing_proposal_id: GenericUUID.optional().openapi({
      description: 'ID of the existing conflicting proposal',
    }),
  })
  .openapi({
    description: 'Error when attempting to create a proposal that conflicts with an existing one',
  });

/**
 * Not found error for proposal operations
 */
export type TimesheetProposalNotFoundError = z.infer<typeof TimesheetProposalNotFoundErrorSchema>;
export const TimesheetProposalNotFoundErrorSchema = z
  .object({
    message: z.string().openapi({
      example: 'Timesheet proposal not found',
    }),
  })
  .openapi({
    description: 'Error when a requested proposal cannot be found',
  });

// ================================================
// AI WEBHOOK SCHEMAS
// ================================================

/**
 * Schema for a single time block in a timesheet request
 */
export type TimesheetEntryItemRequest = z.infer<typeof TimesheetEntryItemRequestSchema>;
export const TimesheetEntryItemRequestSchema = z.object({
  from: z.string().datetime({ offset: true }).openapi({
    description: 'Start datetime with timezone',
  }),
  to: z.string().datetime({ offset: true }).openapi({
    description: 'End datetime with timezone',
  }),
  note: z.string(),
});

export type EmailEntryRequest = z.infer<typeof EmailEntryRequestSchema>;
export const EmailEntryRequestSchema = z.object({
  project_name: z.string().openapi({
    description: 'Name of the project ',
  }),
  project_color: z.string().openapi({
    description: 'Hex color code for the project',
  }),
  client_name: z.string().openapi({
    description: 'Name of the client associated with the project',
  }),
  client_color: z.string().openapi({
    description: 'Hex color code for the client',
  }),
  label_names: z.array(z.string()).openapi({
    description: 'Names of the labels associated with this entry',
  }),
  time_format: z.enum(['12', '24']).openapi({
    description: 'Preferred time format: 12-hour or 24-hour',
  }),
});

/**
 * Schema for a timesheet entry request with grouped time blocks
 * Used for creating timesheet entries via API and webhooks
 */
export type TimesheetEntryRequest = z.infer<typeof TimesheetEntryRequestSchema>;
export const TimesheetEntryRequestSchema = z.object({
  project_id: z.number().int().min(1).nullable().openapi({
    description: 'Timely project ID (null for unassigned)',
  }),
  resource_id: GenericUUID.nullable().optional().openapi({
    description:
      'Reference to an external resource (task, issue, etc.) that this entry is related to',
  }),
  note: z.string().optional(),
  label_ids: z.array(z.number().int().min(1)).optional(),
  entries: z.array(TimesheetEntryItemRequestSchema),
  // Timesheet email specific fields
  email_entry: EmailEntryRequestSchema.nullable().optional().openapi({
    description: 'Optional email entry data if this timesheet entry is associated with an email',
  }),
});

/**
 * Base fields shared by both AI webhook payloads
 */
const AIWebhookBaseFieldsSchema = z.object({
  ticUserId: GenericUUID.openapi({
    description: 'TIC user ID',
  }),
  timelyUserId: z.number().int().openapi({
    description: 'Timely user ID',
  }),
  timelyAccountId: z.number().int().openapi({
    description: 'Timely account ID',
  }),
  date: z.string().date().openapi({
    description: 'Date for the timesheet (YYYY-MM-DD)',
    example: '2024-06-11',
  }),
});

/**
 * Schema for AI timesheet generation webhook payload
 */
export type AITimesheetGeneratedWebhookPayload = z.infer<
  typeof AITimesheetGeneratedWebhookPayloadSchema
>;
export const AITimesheetGeneratedWebhookPayloadSchema = AIWebhookBaseFieldsSchema.extend({
  event: z.enum(['generation_completed', 'generation_failed']).openapi({
    description: 'Event type indicating success or failure',
  }),
  generation_mode: z.enum(['manual', 'auto']).optional().openapi({
    description: 'Mode of generation: manual (user-initiated) or auto (scheduled)',
    example: 'manual',
  }),
  total_input_tokens: z
    .number()
    .int()
    .optional()
    .openapi({ description: 'Total input tokens used', example: 12500 }),
  total_output_tokens: z
    .number()
    .int()
    .optional()
    .openapi({ description: 'Total output tokens used', example: 3400 }),
  entries_input_tokens: z
    .number()
    .int()
    .optional()
    .openapi({ description: 'Input tokens for entry generation', example: 10000 }),
  entries_output_tokens: z
    .number()
    .int()
    .optional()
    .openapi({ description: 'Output tokens for entry generation', example: 2800 }),
  summaries_input_tokens: z
    .number()
    .int()
    .optional()
    .openapi({ description: 'Input tokens for summary generation', example: 2500 }),
  summaries_output_tokens: z
    .number()
    .int()
    .optional()
    .openapi({ description: 'Output tokens for summary generation', example: 600 }),
  entries_cost: z
    .number()
    .optional()
    .openapi({ description: 'Cost in USD for entry generation', example: 0.053 }),
  summaries_cost: z
    .number()
    .optional()
    .openapi({ description: 'Cost in USD for summary generation', example: 0.012 }),
  total_cost: z.number().optional().openapi({ description: 'Total cost in USD', example: 0.065 }),
  provider: z.string().optional().openapi({ description: 'AI model provider', example: 'OpenAI' }),
  effort: z.string().optional().openapi({ description: 'Effort level used', example: 'medium' }),
  interval_minutes: z
    .number()
    .int()
    .optional()
    .openapi({ description: 'Interval minutes setting', example: 30 }),
  note_language: z
    .string()
    .optional()
    .openapi({ description: 'Language for notes', example: 'English' }),
  message: z.string().optional().openapi({
    description: 'Error message if generation failed',
  }),
  timesheet_entries: z.array(TimesheetEntryRequestSchema).optional().openapi({
    description: 'Array of generated timesheet entries (present on success)',
  }),
}).openapi({
  description: 'Webhook payload for AI timesheet generation completion',
  example: {
    ticUserId: '123e4567-e89b-12d3-a456-426614174000',
    timelyUserId: 728755,
    timelyAccountId: 12345,
    date: '2024-06-11',
    event: 'generation_completed',
    generation_mode: 'manual',
    timesheet_entries: [
      {
        project_id: 12345,
        project_name: 'Backend Development',
        project_color: '#3498db',
        note: 'Working on API endpoints',
        label_ids: [123, 456],
        entries: [
          {
            from: '2024-06-11T09:00:00.000Z',
            to: '2024-06-11T11:30:00.000Z',
            note: 'Implemented user authentication',
          },
        ],
      },
    ],
  },
});

/**
 * Schema for AI note regeneration webhook payload
 */
export type AINotesRegeneratedWebhookPayload = z.infer<
  typeof AINotesRegeneratedWebhookPayloadSchema
>;
export const AINotesRegeneratedWebhookPayloadSchema = AIWebhookBaseFieldsSchema.extend({
  event: z.enum(['generation_completed', 'generation_failed']).openapi({
    description: 'Event type indicating success or failure',
  }),
  provider: z
    .string()
    .optional()
    .openapi({ description: 'AI model provider used for regeneration', example: 'OpenAI' }),
  total_input_tokens: z
    .number()
    .int()
    .optional()
    .openapi({ description: 'Total input tokens used', example: 5000 }),
  total_output_tokens: z
    .number()
    .int()
    .optional()
    .openapi({ description: 'Total output tokens used', example: 1200 }),
  total_cost: z.number().optional().openapi({ description: 'Total cost in USD', example: 0.027 }),
  message: z.string().optional().openapi({
    description: 'Error message if regeneration failed',
  }),
  entry_notes: z
    // Keys are proposal entry IDs. Rails timesheet_proposal_entries use bigint PKs,
    // so keys arrive as stringified bigints, not UUIDs — keep the key schema as
    // z.string() (a uuid is still a valid string) to avoid a parse-time drop.
    .record(z.string(), z.string())
    .optional()
    .openapi({
      description: 'Regenerated notes keyed by proposal entry ID (present on success)',
      example: {
        '101': 'Ported stand-up summary into client notes',
        '202': 'Reviewed code and fixed bug in authentication',
      },
    }),
  entry_ids: z
    .array(z.string())
    .optional()
    .openapi({
      description:
        'Explicit proposal entry IDs; when non-empty they take precedence over entry_notes keys. Sent by the engine on failure events, where entry_notes is absent.',
      example: ['101', '202'],
    }),
}).openapi({
  description: 'Webhook payload for AI note regeneration completion',
  example: {
    ticUserId: '123e4567-e89b-12d3-a456-426614174000',
    timelyUserId: 728755,
    timelyAccountId: 12345,
    date: '2024-06-11',
    event: 'generation_completed',
    entry_notes: {
      '123e4567-e89b-12d3-a456-426614174000': 'Ported stand-up summary into client notes',
    },
  },
});

/**
 * Schema for the "AutoSheet auto-send paused" webhook payload.
 *
 * Called by the AI service (ai-timely) when it pauses automatic AutoSheet
 * generation for a user after a stretch of inactivity. Unlike the generation
 * webhooks this has no timesheet `date` — it announces a state change, so it
 * does not extend AIWebhookBaseFieldsSchema (which mandates `date`).
 */
export type AIAutoSendPausedWebhookPayload = z.infer<typeof AIAutoSendPausedWebhookPayloadSchema>;
export const AIAutoSendPausedWebhookPayloadSchema = z
  .object({
    ticUserId: GenericUUID.openapi({ description: 'TIC user ID' }),
    timelyUserId: z.number().int().openapi({ description: 'Timely user ID' }),
    timelyAccountId: z.number().int().openapi({ description: 'Timely account ID' }),
    daysSinceLastActivity: z.number().int().optional().openapi({
      description: 'Days of inactivity that triggered the pause (drives email copy)',
      example: 7,
    }),
  })
  .openapi({
    description: 'Webhook payload announcing that automatic AutoSheet generation was paused',
    example: {
      ticUserId: '123e4567-e89b-12d3-a456-426614174000',
      timelyUserId: 728755,
      timelyAccountId: 12345,
      daysSinceLastActivity: 7,
    },
  });

// ================================================
// DURABLE BACKFILL — internal-only, service-role read ops
// ================================================
//
// These models back the two INTERNAL-ONLY service-role read ops the Rails
// durable eager-backfill coordinator/materializer consumes. They are wired to
// an internal-only contract router (see contract.internal.timesheet_proposals)
// that is deliberately NOT assembled into the public `apiSpec`.

/**
 * Query for the keyset backfill-candidates enumeration.
 *
 * `statuses` is a CSV of proposal statuses (default `pending,accepted`) parsed
 * by the handler. `cursor` is an opaque base64 of `{date, id}` minted by a
 * previous page's `next_cursor`; absent ⇒ first page.
 */
export type BackfillCandidatesQuery = z.infer<typeof BackfillCandidatesQuerySchema>;
export const BackfillCandidatesQuerySchema = z
  .object({
    timely_account_id: GenericTimelyAccountId.optional(),
    timely_user_id: GenericTimelyUserId.optional(),
    statuses: z.string().optional().openapi({
      description:
        "Comma-separated proposal statuses to include; defaults to 'pending,accepted' when omitted.",
      example: 'pending,accepted',
    }),
    limit: z.coerce.number().int().min(1).max(1000).default(500).openapi({
      description: 'Max survivors to return in this page (1–1000).',
      example: 500,
    }),
    cursor: z.string().optional().openapi({
      description:
        'Opaque keyset cursor (base64 of {date,id}) from a prior page. Omit for the first page.',
    }),
  })
  .openapi({ description: 'Query for the backfill-candidates keyset enumeration' });

/**
 * One deduplicated survivor per `(timely_account_id, timely_user_id, date)`.
 * The survivor is chosen accepted-first at the source (see the candidates
 * query's window function), so a same-day pending+accepted pair yields the
 * accepted row here.
 */
export type BackfillCandidate = z.infer<typeof BackfillCandidateSchema>;
export const BackfillCandidateSchema = z
  .object({
    id: GenericUUID,
    timely_account_id: GenericTimelyAccountId,
    timely_user_id: GenericTimelyUserId,
    date: z.string().date().openapi({
      description: 'Proposal date (YYYY-MM-DD)',
      example: '2024-06-11',
    }),
    status: TimesheetProposalStatusSchema,
    updated_at: DateTimeTzSchema.nullable().openapi({
      description: 'When the proposal was last updated (null if never updated)',
    }),
  })
  .openapi({
    description: 'A deduplicated backfill candidate (one survivor per account/user/date)',
  });

export type BackfillCandidatesResponse = z.infer<typeof BackfillCandidatesResponseSchema>;
export const BackfillCandidatesResponseSchema = z
  .object({
    candidates: z.array(BackfillCandidateSchema),
    next_cursor: z.string().nullable().openapi({
      description: 'Cursor for the next page, or null when this is the last page.',
    }),
    // SCOPE-WIDE (not page-bounded): every date in the requested (account,user)
    // scope that has MORE THAN ONE accepted proposal. The survivor dedupe keeps
    // exactly one accepted-first row per (account,user,date), so a 2nd accepted
    // proposal for that day is silently dropped from `candidates`. Surfacing the
    // dates here lets the Rails coordinator fail the backfill gate closed on a
    // nonzero set (L3), the same fail-closed principle applied to L4 — a 2nd
    // accepted proposal can never be lost without operator sign-off.
    two_plus_accepted_dates: z.array(z.string().date()).openapi({
      description:
        'ISO date strings (YYYY-MM-DD) in the requested (account,user) scope with more than one accepted proposal. Scope-wide, independent of keyset pagination. Empty when no day has >1 accepted proposal.',
      example: ['2024-06-11'],
    }),
  })
  .openapi({ description: 'A page of deduplicated backfill candidates' });

/**
 * Body for the batch full-bundle fetch. Mirrors the bulk-update body shape;
 * capped at 50 ids per request to bound the read.
 */
export type BackfillBundleRequest = z.infer<typeof BackfillBundleRequestSchema>;
export const BackfillBundleRequestSchema = z
  .object({
    proposal_ids: z
      .array(GenericUUID)
      .min(1)
      .max(50)
      .openapi({
        description: 'Proposal UUIDs to fetch full bundles for (1–50).',
        example: ['123e4567-e89b-12d3-a456-426614174000'],
      }),
  })
  .openapi({ description: 'Request body for the batch backfill-bundle fetch' });

export type BackfillBundleResponse = z.infer<typeof BackfillBundleResponseSchema>;
export const BackfillBundleResponseSchema = z
  .object({
    bundles: z.array(TimesheetProposalSchema).openapi({
      description:
        'Full proposal bundles (head + entries[] + items[]) for the found ids. Order is not guaranteed; match by id.',
    }),
    not_found: z.array(GenericUUID).openapi({
      description: 'Requested ids that had no matching proposal row.',
    }),
  })
  .openapi({ description: 'Batch backfill-bundle fetch response' });
